/**
 * Notification API — Layer 1 of the three-layer TanStack pattern.
 *
 * Pure async functions, no TanStack, no React imports.
 */
import { apiClient } from "@/shared/lib/axios";
import { NOTIFICATIONS } from "@/shared/lib/apiPaths";

export interface Notification {
  id: string;
  eventKey: string;
  titleAr: string;
  titleEn: string;
  bodyAr: string;
  bodyEn: string;
  url: string;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationCenterResponse {
  notifications: Notification[];
  hasUnread: boolean;
}

export interface NotificationListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Notification[];
}

export async function notificationHasUnread(): Promise<boolean> {
  try {
    const { data } = await apiClient.get<{ detail: string; count: number }>(
      `${NOTIFICATIONS}/unread-check`
    );
    return data.count > 0;
  } catch {
    return false;
  }
}

export async function notificationInbox(params?: {
  cursor?: string;
  pageSize?: number;
}): Promise<NotificationListResponse> {
  const { data } = await apiClient.get<NotificationListResponse>(
    `${NOTIFICATIONS}/`,
    { params }
  );
  return data;
}

export async function notificationCenter(): Promise<NotificationCenterResponse> {
  try {
    const [checkRes, listRes] = await Promise.all([
      apiClient.get<{ detail: string; count: number }>(
        `${NOTIFICATIONS}/unread-check`
      ),
      apiClient.get<NotificationListResponse>(
        `${NOTIFICATIONS}/`,
        { params: { pageSize: 10 } }
      ),
    ]);
    return {
      hasUnread: checkRes.data.count > 0,
      notifications: listRes.data.results,
    };
  } catch {
    return { hasUnread: false, notifications: [] };
  }
}

export async function notificationMarkRead(
  ids: string[]
): Promise<{ markedRead: number }> {
  await Promise.all(
    ids.map((id) =>
      apiClient.post(`${NOTIFICATIONS}/${id}/read`)
    )
  );
  return { markedRead: ids.length };
}

export async function notificationMarkAllRead(): Promise<{ markedRead: number }> {
  const { data } = await apiClient.post<{ count: number }>(
    `${NOTIFICATIONS}/read-all`
  );
  return { markedRead: data.count };
}